import { useEffect, useState } from 'react';
import dynamic from 'next/dynamic';

const Lottie = dynamic(
  () => import('lottie-react'),
  { ssr: false }
);
// next
import { useRouter } from 'next/router';
// mui
import { styled } from '@mui/material/styles';
import {
  Box,
  Button,
  Container,
  Typography,
  IconButton,
  Stack,
  Fade,
  Chip,
  Card,
  CardContent,
  Skeleton,
  Tooltip,
  Pagination,
} from '@mui/material';
// hooks
import useAuth from '../../hooks/useAuth';
import useResponsive from '../../hooks/useResponsive';
// components
import Label from '../../components/Label';
import Page from '../../components/Page';
import Iconify from '../../components/Iconify';
import Image from '../../components/Image';
import DIOText from '../../components/DIOText';
import SvgIconStyle from '../../components/SvgIconStyle';
import {
  ScoutTabs,
} from "./";
import {
  FliersProfileCard,
  FliersDetail,
  FliersPositionCards,
} from '../../sections/fliers';
import findingCrews from '../../assets/lottie/finding-crews.json';
// swr
import useSWR from 'swr';
// utils
import axiosSWRFetcher from '../../utils/swrFetcher';
// routes
import { PATH_SCOUT, PATH_FLIERS, PATH_JOBS } from '../../routes/paths';

const BlueText = styled('span')(({ theme }) => ({
  color: theme.palette.secondary.main,
}));

// ----------------------------------------------------------------------

const hideScrollbar = {
  msOverflowStyle: 'none',
  scrollbarWidth: 'none',
  overflowY: 'scroll',
  '&::-webkit-scrollbar': {
    display: 'none',
  },
};

export default function EnterprisePage() {
  const { user } = useAuth();
  const router = useRouter();
  const { query } = router;
  const [depth, setDepth] = useState(0);
  const [saveSelected, setSaveSelected] = useState(false);
  const [queryParams, setQueryParams] = useState('');
  const [selectedGroup, setSelectedGroup] = useState({});
  const [selectedRole, setSelectedRole] = useState({});
  const [selectedProfileID, setSelectedProfileID] = useState(null);

  const [page, setPage] = useState(1);
  const [pageQuery, setPageQuery] = useState('page=1&');

  const smUp = useResponsive('up', 'sm');
  const lgUp = useResponsive('up', 'lg');
  const splitByResponsive = lgUp ? 3 : smUp ? 2 : 1;

  const companyId = user?.employee?.company?.[0].id;
  const canUseEnterprise = user?.employee?.company?.[0].scout_plan === "enterprise";
  const { data: follower, isValidating: followerValidating } = useSWR((depth === 0 && companyId) ? `/api/company/${companyId}/fliers-followers/` : null, axiosSWRFetcher, {errorRetryInterval: 2000, revalidateOnFocus: false});
  const { data: fliersCrewGroup, isValidating: groupValidating } = useSWR(companyId ? `/api/company/${companyId}/fliers/group/` : null, axiosSWRFetcher, {errorRetryInterval: 2000, revalidateOnFocus: false});
  const { data: fliersPaginatedData, isValidating } = useSWR((canUseEnterprise && fliersCrewGroup) ? `/api/company/${companyId}/fliers/?${pageQuery}${queryParams}` : null, axiosSWRFetcher, {errorRetryInterval: 2000, revalidateOnFocus: false});
  const { data: crewProfile, isLoading: crewProfileValidating, mutate: crewProfileMutate } = useSWR((companyId && selectedProfileID) ? `/api/company/${companyId}/fliers/${selectedProfileID}/` : null, axiosSWRFetcher, {errorRetryInterval: 2000, revalidateOnFocus: false});

  const toggleSaveSelected = () => {
    if (saveSelected) {
      setSaveSelected(false);
      setQueryParams('');
    } else {
      setSaveSelected(true);
      setQueryParams('saved=y');
    }
  };

  const selectGroup = (group) => {
    if (!canUseEnterprise) return ;
    if (group.id) {
      router.push({pathname: PATH_SCOUT.root, query: {group: group.id}});
    } else {
      router.push({pathname: PATH_SCOUT.root, query: {name: group.name, position: group.position}});
    }
  };

  const selectRole = (role) => {
    if (role) {
      router.push({pathname: PATH_SCOUT.root, query: {group: selectedGroup.id, role: role.id}});
    } else {
      router.push({pathname: PATH_SCOUT.root, query: {group: selectedGroup.id}});
    }
  };

  const selectProfile = (profile) => {
    if (profile) {
      router.push({pathname: PATH_SCOUT.root, query: {profile: profile.id}});
    }
  };

  const goBackWithFallback = () => {
    const prev = window.location.href;
    const prevPage = page;
    router.back();
    setTimeout(() => {
      if (page) setPage(prevPage);
    }, 10);
    setTimeout(() => {
      if (window.location.href === prev) {
        router.push({pathname: PATH_SCOUT.root});
      }
    }, 1000);
  };

  const goStart = () => {
    const { saved } = query;
    setSaveSelected(saveSelected || saved === 'y');
    setSelectedGroup({});
    setSelectedRole({});
    setSelectedProfileID(null);
    setQueryParams(saveSelected ? 'saved=y' : '');
    setDepth(0);
    setPage(1);
  };

  useEffect(() => {
    setPageQuery(`page=${page}&`);
  }, [page]);

  useEffect(() => {
    if (query && fliersCrewGroup) {
      const { group, name, position, role, profile, saved } = query;
      setSaveSelected(saveSelected || saved === 'y');
      setSelectedGroup({});
      setSelectedRole({});
      setSelectedProfileID(null);
      setQueryParams(saveSelected ? 'saved=y' : '');

      if (!group && !profile && !name) {
        goStart();
      }
      if (group) {
        const selectedGroup = fliersCrewGroup?.find((g) => g.id === parseInt(group));
        if (selectedGroup && !!role) {
          const selectedRole = selectedGroup.roles?.find((r) => r.id === parseInt(role)) || selectedGroup?.children?.map((g) => g.roles?.find((r) => r.id === parseInt(role))).find((r) => r);
          if (selectedRole) {
            setDepth(1);
            setPage(1);
            setSaveSelected(false);
            setSelectedGroup(selectedGroup);
            setSelectedRole(selectedRole);
            setQueryParams(`group=${group}&role=${role}`);
          } else {
            goStart();
          }
        } else if (selectedGroup) {
          setDepth(1);
          setPage(1);
          setSaveSelected(false);
          setSelectedGroup(selectedGroup);
          setQueryParams(`group=${group}`);
        } else {
          goStart();
        }
      } else if (name) {
        const selectedGroup = fliersCrewGroup?.find((g) => g.name === name);
        if (selectedGroup) {
          setDepth(1);
          setPage(1);
          setSaveSelected(false);
          setQueryParams(`position=${position}`);
          setSelectedGroup(selectedGroup);
        }
      }
      if (profile) {
        // profile 로딩 해보고 없으면 돌아가야함
        if (profile) {
          setSaveSelected(false);
          setSelectedProfileID(profile);
          setDepth(2);
        } else {
          goStart();
        }
      }
    } else if (query && !canUseEnterprise) {
      const { profile } = query;
      if (profile) {
        setSaveSelected(false);
        setSelectedProfileID(parseInt(profile));
        setDepth(2);
      } else {
        goStart();
      }
    }
  }, [query, fliersCrewGroup]);

  return (
    <>
      {depth === 0 && (  // depth 0 - all
      <Page title="Scout">
        <Box sx={{ px: { xs: 3, md: 5 }, mx: "auto", pb: { xs: 2, md: 4 }, backgroundColor: 'white' }} maxWidth="lg">
          <Stack direction="row" spacing={1} alignItems="flex-end" sx={{ mb: 1 }}>
            <Image src={"/icons/scout-logo.svg"} sx={{ width: smUp ? "94px" : "82.25px", height: smUp ? "16px" : "14px", color: "primary.main" }} />
            <Typography variant="caption" sx={{ lineHeight: "12.8px" }}>
              커피챗을 제안해보세요!
            </Typography>
          </Stack>
          <ScoutTabs tab="recommend" />
          {groupValidating ? (
            <Skeleton variant="rectangular" height={150} />
          ) : (
            <FliersPositionCards groups={fliersCrewGroup} selectGroup={selectGroup} />
          )}
        </Box>
        {/* 팔로우 리스트 */}
        <Box
          sx={{
            px: { xs: 3, md: 5 },
            py: { xs: 2, md: 4 },
            bgcolor: "#eff6fe",
          }}
        >
          <Container maxWidth="lg" sx={{ px: 0 }}>
          <Stack direction={{ xs: "column", sm: "row" }} alignItems={{ xs: "baseline", sm: "flex-start" }} justifyContent="space-between" sx={{ mb: 2 }}>
            <Stack spacing={1}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Typography variant="h6">
                  우리 회사를 팔로우한 크루
                </Typography>
                <Label
                  color={follower?.length ? "info" : "default"}
                  variant="filled"
                >
                  {follower?.length}명
                </Label>
              </Stack>
              <Typography variant="body2">
                우리 회사에 관심을 표시한 크루에요. 크루 상세 정보를 확인하고, 마음에 드는 크루에게 커피챗을 제안해보세요!
              </Typography>
            </Stack>
            {follower?.length > 0 && (
            <Button
              variant="text"
              sx={{ color: "info.dark" }}
              endIcon={<Iconify icon="ic:chevron-right" />}
              onClick={() => router.push({pathname: PATH_FLIERS.follower})}
            >
              팔로우한 크루 전체보기
            </Button>
            )}
          </Stack>
          {/* no follower */}
          {followerValidating && (
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: 'repeat(1, 1fr)',
                  sm: 'repeat(2, 1fr)',
                  lg: 'repeat(3, 1fr)',
                },
                gap: 2,
                rowGap: 3,
              }}
            >
              <Skeleton variant="rectangular" height={200} />
              {smUp && ( <Skeleton variant="rectangular" height={200} /> )}
              {lgUp && ( <Skeleton variant="rectangular" height={200} /> )}
            </Box>
          )}
          {!followerValidating && follower?.length === 0 && (
            <Stack sx={{ width: 1, textAlign: "center" }}>
              <Typography variant={smUp ? "h6" : "subtitle2"} gutterBottom>
                아직 우리 회사를 팔로우하는 크루가 없어요.
              </Typography>
              <Typography variant={smUp ? "body1" : "body2"} paragraph>
                회사 정보를 업데이트하고,{!smUp && <br/>} 크루들의 팔로우를 받아보세요!
              </Typography>
              <Box>
                <Button
                  color="info"
                  endIcon={<Iconify icon="ic:chevron-right" />}
                  onClick={() => router.push({pathname: PATH_SCOUT.profile})}
                >
                  우리 회사 정보 업데이트하기
                </Button>
              </Box>
            </Stack>
          )}
          {!followerValidating && follower?.length > 0 && (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: 'repeat(1, 1fr)',
                sm: 'repeat(2, 1fr)',
                lg: 'repeat(3, 1fr)',
              },
              gap: 2,
              rowGap: 3,
            }}
          >
            {/* follower cards */}
            {follower?.slice(0, splitByResponsive).map((crew, index) => (
              <FliersProfileCard profile={crew} key={index} selectProfile={selectProfile} />
            ))}
          </Box>
          )}
          </Container>
        </Box>
        {/* 크루 리스트 */}
        <Box
          sx={{
            px: { xs: 3, md: 5 },
            py: { xs: 2, md: 3 },
            backgroundColor: 'grey.200',
            minHeight: smUp ? 'calc(100vh - 745px)' : 'calc(100vh - 684px)'
          }}
        >
          <Container maxWidth="lg" sx={{ px: 0 }}>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
            <Typography variant="h5">
              전체 보기
            </Typography>
            <Tooltip title="찜한 크루만 보기" placement="top" arrow>
              <IconButton size="medium" color="primary" onClick={toggleSaveSelected}>
                <Iconify icon={saveSelected ? 'eva:heart-fill' : 'eva:heart-outline'} />
              </IconButton>
            </Tooltip>
          </Stack>
          {fliersPaginatedData?.count === 0 && (
            <Stack sx={{ my: 3, width: 1 }} alignItems="center">
              <Typography variant="h6" gutterBottom>
                커피챗 가능한 <BlueText>크루</BlueText>가 없습니다.
              </Typography>
              <Typography>
                공고를 작성해주시면
              </Typography>
              <Typography paragraph>
                평소처럼 <DIOText />가 크루를 찾아드릴게요.
              </Typography>
              <Button size="medium" variant="contained" sx={{ px: 8 }} onClick={() => router.push({pathname: PATH_JOBS.root, query: {new: 'y'} })}>
                공고 작성하기
              </Button>
              <Lottie
                animationData={findingCrews}
                loop={true}
                style={{ width: 300, height: 300, display: 'block', marginLeft: 'auto', marginRight: 'auto' }}
              />
            </Stack>
          )}
          <Fade in={depth === 0}>
          <Box>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: 'repeat(1, 1fr)',
                sm: 'repeat(2, 1fr)',
                lg: 'repeat(3, 1fr)',
              },
              gap: 2,
              rowGap: 3,
            }}
          >
            {isValidating && (!fliersPaginatedData || fliersPaginatedData.count === 0) && Array.from(Array(12))?.map((e, i) => (
              <Card key={i}>
                <CardContent>
                  <Stack spacing={1}>
                    <Skeleton variant="rectangular" height={80} />
                    <Skeleton variant="rectangular" height={50} />
                  </Stack>
                </CardContent>
              </Card>
            ))}
            {/* crew cards */}
            {fliersPaginatedData?.results?.map((crew, index) => (
              <FliersProfileCard profile={crew} key={index} selectProfile={selectProfile} />
            ))}
          </Box>
          {!isValidating && fliersPaginatedData?.count > 0 && (
            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
              <Pagination
                size={smUp ? "medium" : "small"}
                count={fliersPaginatedData?.page_count || 0}
                page={page}
                onChange={(e, v) => setPage(v)}
              />
            </Box>
          )}
          </Box>
          </Fade>
          </Container>
        </Box>
      </Page>
      )}

      {depth === 1 && (  // depth 1 - crew list by position
      <Page title="Scout" sx={{ height: smUp ? 'calc(100vh - 32px)' : 'calc(100vh - 16px)', backgroundColor: 'grey.200' }}>
        <Box sx={{ px: { xs: 3, sm: 5 }, backgroundColor: 'white' }}>
          <Container maxWidth="lg" sx={{ px: 0 }}>
          <IconButton color="inherit" edge="start" sx={{ mt: 0.5 }} onClick={() => router.push({pathname: PATH_SCOUT.root})}>
            <SvgIconStyle src={`/icons/chevron-left.svg`} sx={{ width: 32, height: 32, color: "primary.main" }} />
          </IconButton>
          <Stack sx={{ py: 1 }} direction="row" spacing={1} alignItems="center">
            <Typography variant="h4">
              {selectedGroup?.name} {selectedGroup?.get_position_display}
            </Typography>
          </Stack>
          </Container>
        </Box>
        <Box sx={{ py: 2 , px: { xs: 3, sm: 5 }, backgroundColor: 'white', ...hideScrollbar }}>
          <Container maxWidth="lg" sx={{ px: 0 }}>
          <Stack direction="row" spacing={2}>
            {!selectedGroup?.position && (
            <Chip
              label="전체"
              variant={!selectedRole?.id ? "filled" : "outlined"}
              sx={{
                fontWeight: !selectedRole?.id ? "bold" : "normal",
                '&:hover': {
                  cursor: 'pointer',
                  bgcolor: 'grey.300',
                }
              }}
              onClick={() => selectRole(null)}
            />
            )}
            {/* 역할 필터 */}
            {selectedGroup?.roles?.length > 1 && selectedGroup?.roles?.map((role, index) => (
              <RoleFilterChip role={role} selectedRole={selectedRole} selectRole={selectRole} key={index} />
            ))}
            {selectedGroup?.children?.length > 0 && selectedGroup?.children?.map((child) => (
              child.roles?.map((role, index) => (
                <RoleFilterChip role={role} selectedRole={selectedRole} selectRole={selectRole} key={index} />
              ))
            ))}
          </Stack>
          </Container>
        </Box>
        <Fade in={depth === 1}>
        <Box
          sx={{
            width: 1,
            px: { xs: 3, sm: 5 },
            py: { xs: 2, sm: 3 },
            backgroundColor: 'grey.200',
          }}
        >
          <Container maxWidth="lg" sx={{ px: 0 }}>
          {isValidating && !fliersPaginatedData && (
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: 'repeat(1, 1fr)',
                  sm: 'repeat(2, 1fr)',
                  lg: 'repeat(3, 1fr)',
                },
                gap: 2,
                rowGap: 3,
              }}
            >
            {Array.from(Array(3))?.map((e, i) =>
              <Card key={i}>
                <CardContent>
                  <Stack spacing={1}>
                    <Skeleton variant="rectangular" height={80} />
                    <Skeleton variant="rectangular" height={50} />
                  </Stack>
                </CardContent>
              </Card>
            )}
            </Box>
          )}
          {fliersPaginatedData?.count === 0 && (
            <Stack sx={{ my: 3, width: 1 }} alignItems="center">
              <Typography variant="h6" gutterBottom>
                예약 가능한 <BlueText>크루</BlueText>가 없습니다.
              </Typography>
              <Typography>
                공고를 작성해주시면
              </Typography>
              <Typography paragraph>
                평소처럼 <DIOText />가 크루를 찾아드릴게요.
              </Typography>
              <Button size="medium" variant="contained" sx={{ px: 8 }} onClick={() => router.push({pathname: PATH_JOBS.root, query: {new: 'y'} })}>
                공고 작성하기
              </Button>
              <Lottie
                animationData={findingCrews}
                loop={true}
                style={{ width: 300, height: 300, display: 'block', marginLeft: 'auto', marginRight: 'auto' }}
              />
            </Stack>
          )}
          {fliersPaginatedData?.results?.length > 0 && (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: 'repeat(1, 1fr)',
                sm: 'repeat(2, 1fr)',
                lg: 'repeat(3, 1fr)',
              },
              gap: 2,
              rowGap: 3,
            }}
          >
            {/* crew cards */}
            {fliersPaginatedData?.results?.map((crew, index) => (
              <FliersProfileCard profile={crew} key={index} selectProfile={selectProfile} />
            ))}
          </Box>
          )}
          {!isValidating && fliersPaginatedData?.count > 0 && (
            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
              <Pagination
                size={smUp ? "medium" : "small"}
                count={fliersPaginatedData?.page_count || 0}
                page={page}
                onChange={(e, v) => setPage(v)}
              />
            </Box>
          )}
          </Container>
        </Box>
        </Fade>
      </Page>
      )}

      {depth === 2 && (  // depth 2 - crew detail
      <Page title="Crew">
        <Container maxWidth="md" sx={{ px: { xs: 3, sm: 2 }, pb: 2 }}>
          <IconButton color="inherit" edge="start" sx={{ mt: 0.5 }} onClick={goBackWithFallback}>
            <SvgIconStyle src={`/icons/chevron-left.svg`} sx={{ width: 32, height: 32, color: "primary.main" }} />
          </IconButton>
          {crewProfileValidating && (
            <Stack spacing={3} sx={{ mt: 1 }}>
              <Skeleton variant="rectangular" height={120} sx={{ borderRadius: 1 }} />
              <Skeleton variant="rectangular" height={50} sx={{ borderRadius: 1 }} />
              <Skeleton variant="rectangular" height={500} sx={{ borderRadius: 1 }} />
            </Stack>
          )}
          {!crewProfileValidating && (
            <FliersDetail profile={crewProfile} reload={crewProfileMutate} />
          )}
        </Container>
      </Page>
      )}
    </>
  )
};

function RoleFilterChip ({ role, selectedRole, selectRole }) {
  return (
    <Chip
      label={role.short_name}
      variant={selectedRole?.id === role.id ? "filled" : "outlined"}
      sx={{
        fontWeight: selectedRole?.id === role.id ? "bold" : "normal",
        '&:hover': {
          cursor: 'pointer',
          bgcolor: 'grey.300',
        }
      }}
      onClick={() => selectRole(role)}
    />
  )
}